var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import './MonitorMenu.scss';
import classNames from 'classnames';
import { createAsyncSelectors } from '@lib/selectors';
import { MonitorApplication } from './MonitorApplication';
import { MonitorDetails } from './MonitorDetails';
import { MonitorNotifications } from './MonitorNotifications';
import { Space } from 'antd';
import { useDataSecurityMonitors } from '@hooks/data-security/useDataSecurityMonitors';
import { VisualPicker } from '@ui/form-items';
import { When } from 'react-if';
import { ActionTemplateCard, labelMetaLookup } from '../ActionTemplateCard';
import { ClassificationLevel, coreActions, DataSecurityMonitorType, FeatureFlag, getRecommendedActionTemplateById, MonitoringWindowType } from '@sonar-software/sonar-core';
import { COPY_SPECIFIC, SETTINGS_DATA_SECURITY_COPY } from '@sonar-software/sonar-ts-constants';
import { isEqual, isNumber } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAction, useAsyncState, useDataSecurityApps, useFeatureFlag, useFeatureFlags, useOnMount, usePrevious } from '@hooks';
const selectors = createAsyncSelectors((state) => state.core.dataSecurity.actionTemplates.get);
export const defaultConfig = {
    applicationId: undefined,
    applicationIds: [],
    monitorConfig: {
        complianceCategories: undefined,
        dataSensitivityLevels: undefined,
        enableOnWeekends: undefined,
        monitorTypeId: undefined,
        monitoringWindow: undefined,
        ruleType: undefined,
        rules: undefined
    },
    name: '',
    overrideCount: undefined,
    stakeholderIds: [],
    templateId: undefined
};
export const monitorMenuConfigs = [
    {
        active: true,
        applicationRequired: true,
        default: Object.assign(Object.assign({}, defaultConfig), { monitorConfig: Object.assign(Object.assign({}, defaultConfig.monitorConfig), { monitorTypeId: DataSecurityMonitorType.NewObjectAndFieldAccess }), name: SETTINGS_DATA_SECURITY_COPY.OBJECT_AND_FIELD_ACCESS }),
        featureFlag: FeatureFlag.DataSecurityDashboard,
        intrinsic: true,
        max: 1,
        title: SETTINGS_DATA_SECURITY_COPY.OBJECT_AND_FIELD_ACCESS,
        type: DataSecurityMonitorType.NewObjectAndFieldAccess
    },
    {
        active: true,
        applicationRequired: true,
        default: Object.assign(Object.assign({}, defaultConfig), { monitorConfig: Object.assign(Object.assign({}, defaultConfig.monitorConfig), { enableOnWeekends: false, monitorTypeId: DataSecurityMonitorType.Uptime, monitoringWindow: MonitoringWindowType.Daily }) }),
        featureFlag: FeatureFlag.DataSecurityDashboard,
        intrinsic: false,
        max: 1,
        title: SETTINGS_DATA_SECURITY_COPY.UPTIME,
        type: DataSecurityMonitorType.Uptime
    },
    {
        active: true,
        applicationRequired: true,
        default: Object.assign(Object.assign({}, defaultConfig), { monitorConfig: Object.assign(Object.assign({}, defaultConfig.monitorConfig), { monitorTypeId: DataSecurityMonitorType.NewUser }) }),
        featureFlag: FeatureFlag.DataSecurityDashboard,
        intrinsic: false,
        max: 1,
        title: SETTINGS_DATA_SECURITY_COPY.NEW_USER,
        type: DataSecurityMonitorType.NewUser
    },
    {
        active: true,
        applicationRequired: false,
        default: Object.assign(Object.assign({}, defaultConfig), { monitorConfig: Object.assign(Object.assign({}, defaultConfig.monitorConfig), { monitorTypeId: DataSecurityMonitorType.ConfigurableFieldAccess }) }),
        featureFlag: FeatureFlag.DataSecurityDashboard,
        intrinsic: false,
        max: -1,
        title: SETTINGS_DATA_SECURITY_COPY.OBJECT_AND_FIELD_ACCESS,
        type: DataSecurityMonitorType.ConfigurableFieldAccess
    },
    {
        active: true,
        applicationRequired: false,
        default: Object.assign(Object.assign({}, defaultConfig), { monitorConfig: Object.assign(Object.assign({}, defaultConfig.monitorConfig), { monitorTypeId: DataSecurityMonitorType.SetupAuditTrail, ruleType: 'and', rules: [{
                        condition: 'classification',
                        value: [ClassificationLevel.Critical]
                    }] }) }),
        featureFlag: FeatureFlag.SetupAuditTrail,
        intrinsic: false,
        max: -1,
        title: SETTINGS_DATA_SECURITY_COPY.SETUP_AUDIT_TRAIL,
        type: DataSecurityMonitorType.SetupAuditTrail
    }
];
export function getMenuItems(monitors, isEnabled, applicationIds, type = 'all') {
    const configList = monitorMenuConfigs.filter((x) => !x.intrinsic &&
        x.active &&
        (x.featureFlag && isEnabled(x.featureFlag)) &&
        ((type === 'applicationRequired' && x.applicationRequired) ||
            (type === 'noApplicationRequired' && !x.applicationRequired) ||
            (type === 'all')));
    const menuItems = configList.reduce((result, config) => {
        if (config.max === -1) {
            config.disabled = false;
        }
        else {
            config.disabled = config.applicationRequired && applicationIds ?
                applicationIds.every((appId) => monitors.filter((monitor) => {
                    var _a;
                    return ((_a = monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId) === config.type &&
                        monitor.applicationId === appId;
                }).length >= config.max) :
                monitors.filter((monitor) => { var _a; return ((_a = monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId) === config.type; }).length >= config.max;
        }
        return [...result, config];
    }, []);
    return menuItems;
}
function getTemplateDetails(templateId) {
    return __awaiter(this, void 0, void 0, function* () {
        const result = yield getRecommendedActionTemplateById({
            templateId
        });
        return result.data;
    });
}
;
export const MonitorMenu = ({ editing = true, fallbackMonitor, fieldId, hideApplications = false, menuOptions, monitor, onChange, overrideMonitors, showRecommendedActionTemplates = false }) => {
    var _a, _b, _c, _d, _e, _f;
    const monitors = useDataSecurityMonitors();
    const applications = useDataSecurityApps();
    const { isEnabled } = useFeatureFlags();
    const isTemplatesEnabled = useFeatureFlag(FeatureFlag.RecommendedActionTemplates);
    const load = useAction(coreActions.requestGetCoreDataSecurityActionTemplates);
    const { data } = useAsyncState(selectors);
    useOnMount(() => {
        if (isTemplatesEnabled && !data) {
            load();
        }
    });
    const isExistingMonitor = Boolean((monitor === null || monitor === void 0 ? void 0 : monitor.id) || !editing || (fieldId &&
        (((overrideMonitors === null || overrideMonitors === void 0 ? void 0 : overrideMonitors.length) === 1 &&
            ((_a = overrideMonitors[0].monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId) === ((_b = monitor === null || monitor === void 0 ? void 0 : monitor.monitorConfig) === null || _b === void 0 ? void 0 : _b.monitorTypeId)) ||
            (((_c = monitor === null || monitor === void 0 ? void 0 : monitor.monitorConfig) === null || _c === void 0 ? void 0 : _c.monitorTypeId) && ((_d = monitorMenuConfigs
                .find((x) => { var _a; return x.type === ((_a = monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId); })) === null || _d === void 0 ? void 0 : _d.intrinsic)))));
    const monitorTypeOptions = useMemo(() => {
        var _a, _b, _c, _d;
        if (isExistingMonitor) {
            return [{
                    disabled: false,
                    label: (_b = (_a = monitorMenuConfigs.find((x) => { var _a; return x.type === ((_a = monitor === null || monitor === void 0 ? void 0 : monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId); })) === null || _a === void 0 ? void 0 : _a.title) !== null && _b !== void 0 ? _b : '',
                    value: (_d = (_c = monitor === null || monitor === void 0 ? void 0 : monitor.monitorConfig) === null || _c === void 0 ? void 0 : _c.monitorTypeId) !== null && _d !== void 0 ? _d : 1
                }];
        }
        const menuItems = getMenuItems(overrideMonitors !== null && overrideMonitors !== void 0 ? overrideMonitors : monitors, isEnabled, hideApplications && (monitor === null || monitor === void 0 ? void 0 : monitor.applicationId) ?
            [monitor === null || monitor === void 0 ? void 0 : monitor.applicationId] :
            applications.map((app) => app.applicationId), menuOptions);
        return menuItems.map((config) => ({
            disabled: config.disabled,
            label: config.title,
            value: config.type
        }));
    }, [
        applications,
        hideApplications,
        isExistingMonitor,
        isEnabled,
        menuOptions,
        monitor,
        monitors,
        overrideMonitors
    ]);
    const defaultMonitor = useMemo(() => {
        var _a, _b, _c;
        if ((_a = monitor === null || monitor === void 0 ? void 0 : monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId) {
            return monitor;
        }
        const validOptions = monitorTypeOptions.filter((x) => x.disabled === false);
        const tempMonitor = validOptions.length === 1 && ((_b = monitorMenuConfigs.find((x) => x.type === validOptions[0].value)) === null || _b === void 0 ? void 0 : _b.default) ? Object.assign({}, (_c = monitorMenuConfigs.find((x) => x.type === validOptions[0].value)) === null || _c === void 0 ? void 0 : _c.default) : Object.assign({}, defaultConfig);
        if (monitor === null || monitor === void 0 ? void 0 : monitor.applicationId) {
            tempMonitor.applicationId = monitor.applicationId;
        }
        return tempMonitor;
    }, [monitor, monitorTypeOptions]);
    const [currentMonitor, setCurrentMonitor] = useState(defaultMonitor);
    const prevDefault = usePrevious(defaultMonitor, defaultMonitor);
    const selectedMonitorMenuConfig = useMemo(() => monitorMenuConfigs.find((x) => { var _a; return x.type === ((_a = currentMonitor === null || currentMonitor === void 0 ? void 0 : currentMonitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId); }), [currentMonitor]);
    const setSelectedMonitor = useCallback((newMonitor) => {
        if (!isEqual(newMonitor, currentMonitor)) {
            setCurrentMonitor(newMonitor);
            if (onChange) {
                onChange(newMonitor);
            }
        }
    }, [currentMonitor, onChange]);
    useEffect(() => {
        if (!isEqual(defaultMonitor, prevDefault)) {
            setSelectedMonitor(defaultMonitor);
        }
    }, [defaultMonitor, prevDefault, setSelectedMonitor]);
    const handleOnTypeChange = useCallback((type) => {
        var _a, _b;
        if (type === undefined) {
            setSelectedMonitor(fallbackMonitor !== null && fallbackMonitor !== void 0 ? fallbackMonitor : defaultConfig);
            return;
        }
        const newMonitor = Object.assign({}, (_b = (_a = monitorMenuConfigs.find((config) => config.type === type)) === null || _a === void 0 ? void 0 : _a.default) !== null && _b !== void 0 ? _b : defaultConfig);
        if (defaultMonitor === null || defaultMonitor === void 0 ? void 0 : defaultMonitor.applicationId) {
            newMonitor.applicationId = defaultMonitor.applicationId;
        }
        setSelectedMonitor(newMonitor);
    }, [defaultMonitor.applicationId, fallbackMonitor, setSelectedMonitor]);
    const handleOnTemplateChange = useCallback((templateId) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!templateId) {
            if ((_a = monitor === null || monitor === void 0 ? void 0 : monitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId) {
                handleOnTypeChange(monitor.monitorConfig.monitorTypeId);
            }
            else {
                setSelectedMonitor(fallbackMonitor !== null && fallbackMonitor !== void 0 ? fallbackMonitor : defaultConfig);
            }
            return;
        }
        const templateDetails = yield getTemplateDetails(templateId);
        if (!templateDetails) {
            return;
        }
        const newMonitor = {
            applicationIds: (_b = templateDetails.integratedApplicationIds) !== null && _b !== void 0 ? _b : [],
            monitorConfig: Object.assign({}, templateDetails.configuration),
            overrideCount: templateDetails.overrideCount,
            templateId
        };
        setSelectedMonitor(newMonitor);
    }), [fallbackMonitor, setSelectedMonitor]);
    const templateTypeOptions = useMemo(() => {
        var _a, _b;
        const selectedType = (_a = currentMonitor.monitorConfig) === null || _a === void 0 ? void 0 : _a.monitorTypeId;
        const templateList = selectedType ?
            data === null || data === void 0 ? void 0 : data.filter((template) => { var _a; return selectedType === ((_a = labelMetaLookup[template.label]) === null || _a === void 0 ? void 0 : _a.monitorType); }) :
            data === null || data === void 0 ? void 0 : data.filter((template) => template.type === 'Monitor');
        return (_b = templateList === null || templateList === void 0 ? void 0 : templateList.map((template) => ({
            disabled: template.disabled,
            label: React.createElement(ActionTemplateCard, Object.assign({ key: template.id, onClick: () => handleOnTemplateChange(template.id) }, template)),
            value: template.id
        }))) !== null && _b !== void 0 ? _b : [];
    }, [currentMonitor, data, handleOnTemplateChange]);
    return (React.createElement(Space, { className: classNames({ 'hide-first': menuOptions === 'hide' }), direction: 'vertical' },
        React.createElement(VisualPicker, { disabled: isExistingMonitor, groupClassname: 'menu-group', groupItemClassname: 'menu-group-item', hidden: menuOptions === 'hide', message: COPY_SPECIFIC.NO_UNMONITORED_APPS_FOUND, name: fieldId === undefined ? ['monitorConfig', 'monitorTypeId'] :
                [fieldId, 'monitorConfig', 'monitorTypeId'], onChange: handleOnTypeChange, options: monitorTypeOptions, title: isExistingMonitor ?
                SETTINGS_DATA_SECURITY_COPY.MONITOR_TYPE :
                SETTINGS_DATA_SECURITY_COPY.SELECT_MONITOR_TYPE, value: (_e = currentMonitor.monitorConfig) === null || _e === void 0 ? void 0 : _e.monitorTypeId }),
        React.createElement(When, { condition: showRecommendedActionTemplates && isTemplatesEnabled && !currentMonitor.id },
            React.createElement(VisualPicker, { groupClassname: 'template-group', groupItemClassname: 'template-group-item', name: fieldId === undefined ? ['templateId'] :
                    [fieldId, 'templateId'], onChange: handleOnTemplateChange, options: templateTypeOptions, title: COPY_SPECIFIC.RECOMMENDED_ACTION_TEMPLATES, value: currentMonitor.templateId })),
        React.createElement(When, { condition: selectedMonitorMenuConfig === null || selectedMonitorMenuConfig === void 0 ? void 0 : selectedMonitorMenuConfig.applicationRequired },
            React.createElement(MonitorApplication, { editing: editing, fieldId: fieldId, hidden: Boolean(monitor === null || monitor === void 0 ? void 0 : monitor.id) || hideApplications, monitor: currentMonitor, onChange: (appId) => setSelectedMonitor(Object.assign(Object.assign({}, currentMonitor), { applicationId: appId })) })),
        React.createElement(When, { condition: currentMonitor.templateId ||
                (((_f = currentMonitor.monitorConfig) === null || _f === void 0 ? void 0 : _f.monitorTypeId) &&
                    (isNumber(currentMonitor.applicationId) || !(selectedMonitorMenuConfig === null || selectedMonitorMenuConfig === void 0 ? void 0 : selectedMonitorMenuConfig.applicationRequired))) },
            React.createElement(Space, { direction: 'vertical' },
                React.createElement(MonitorDetails, { editing: editing, fieldId: fieldId, hideName: Boolean(currentMonitor.templateId), monitor: currentMonitor, noMargin: !(selectedMonitorMenuConfig === null || selectedMonitorMenuConfig === void 0 ? void 0 : selectedMonitorMenuConfig.applicationRequired) && menuOptions === 'hide' }),
                React.createElement(MonitorNotifications, { editing: editing, fieldId: fieldId, schedule: currentMonitor === null || currentMonitor === void 0 ? void 0 : currentMonitor.notificationSchedule, stakeholderIds: currentMonitor === null || currentMonitor === void 0 ? void 0 : currentMonitor.stakeholderIds })))));
};
